import { useMemo } from "react";
import { useQuery } from "@rocicorp/zero/react";
import { queries } from "@/lib/queries";

interface UseCanPostArgs {
  channelId: string;
  isDM: boolean | undefined;
  postingRestriction: string;
  userId: string | undefined;
}

/**
 * Resolves whether the current viewer can post in `channelId` given
 * `postingRestriction` ("everyone" | "admins_only" | "specific_members").
 *
 * Reads the channel's manager list, posting-permissions allowlist, and
 * the workspace member rows that hold roles. DMs always return `true`.
 *
 * Lifted out of `MessageInput.tsx` so the parent stays under the
 * file-size ceiling and the permission semantics live in one place.
 */
export function useCanPost({
  channelId,
  isDM,
  postingRestriction,
  userId,
}: UseCanPostArgs): boolean {
  const [channelManagers] = useQuery(
    queries.channel_members.managers(channelId),
  );
  const [postingPermissions] = useQuery(
    queries.channel_posting_permissions.byChannel(channelId),
  );
  const [channelData] = useQuery(queries.channels.byId(channelId));
  const wsId = channelData?.workspace_id ?? null;
  const [wsMembersRaw] = useQuery(
    wsId && userId ? queries.workspace_members.byWorkspace(wsId) : null,
  );

  return useMemo(() => {
    if (!userId) return false;
    if (isDM) return true;
    if (postingRestriction === "everyone") return true;

    const isWorkspaceAdmin = (wsMembersRaw ?? []).some(
      (m) =>
        m.user_id === userId &&
        ["primary_owner", "owner", "admin"].includes(m.role),
    );

    if (postingRestriction === "admins_only") {
      const isChannelManager = channelManagers.some(
        (m) => m.user_id === userId,
      );
      return isChannelManager || isWorkspaceAdmin;
    }
    if (postingRestriction === "specific_members") {
      const hasPermission = postingPermissions.some(
        (p) => p.user_id === userId,
      );
      const isManager = channelManagers.some((m) => m.user_id === userId);
      return hasPermission || isManager || isWorkspaceAdmin;
    }
    return true;
  }, [
    userId,
    isDM,
    postingRestriction,
    channelManagers,
    postingPermissions,
    wsMembersRaw,
  ]);
}
